import { Plus, Trash, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KitNum } from "./shared";

export const KitsTab = ({ d, set }) => (
  <div
    className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
    data-testid="drawing-kits"
  >
    <p className="text-xs text-zinc-500">
      One-click cut-out kits for the drawing editor. Each part is placed relative
      to the piece centre — X offset is right(+)/left(−), Y offset is
      front(+)/back(−), in inches.
    </p>
    <div className="mt-4 space-y-4">
      {(d.kits || []).map((kit, ki) => (
        <div
          key={kit.id || ki}
          data-testid={`kit-${ki}`}
          className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4"
        >
          <div className="flex items-center gap-3">
            <Input
              data-testid={`kit-name-${ki}`}
              value={kit.name || ""}
              onChange={(e) =>
                set({
                  kits: d.kits.map((k, idx) =>
                    idx === ki ? { ...k, name: e.target.value } : k,
                  ),
                })
              }
              className="h-11 flex-1 border-zinc-800 bg-zinc-950 text-sm font-semibold"
              placeholder="Kit name"
            />
            <button
              type="button"
              data-testid={`kit-delete-${ki}`}
              onClick={() =>
                set({ kits: d.kits.filter((_, idx) => idx !== ki) })
              }
              className="rounded-lg border border-zinc-700 p-2 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
            >
              <Trash size={14} weight="bold" />
            </button>
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 px-1 text-[9px] font-bold uppercase tracking-widest text-zinc-500">
              <span className="min-w-[150px] flex-1">Part</span>
              <span className="w-16 text-center">W</span>
              <span className="w-16 text-center">H</span>
              <span className="w-16 text-center">X off</span>
              <span className="w-16 text-center">Y off</span>
              <span className="w-8" />
            </div>
            {(kit.parts || []).map((pt, pi) => {
              const setField = (patch) =>
                set({
                  kits: d.kits.map((k, idx) =>
                    idx === ki
                      ? {
                          ...k,
                          parts: k.parts.map((p, j) =>
                            j === pi ? { ...p, ...patch } : p,
                          ),
                        }
                      : k,
                  ),
                });
              return (
                <div
                  key={pi}
                  data-testid={`kit-${ki}-part-${pi}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <Input
                    data-testid={`kit-${ki}-part-${pi}-kind`}
                    value={pt.kind || ""}
                    onChange={(e) => setField({ kind: e.target.value })}
                    className="h-10 min-w-[150px] flex-1 border-zinc-800 bg-zinc-950 text-sm"
                    placeholder="Cut-out kind"
                  />
                  <KitNum
                    testId={`kit-${ki}-part-${pi}-w`}
                    value={pt.w}
                    onChange={(v) => setField({ w: v })}
                  />
                  <KitNum
                    testId={`kit-${ki}-part-${pi}-h`}
                    value={pt.h}
                    onChange={(v) => setField({ h: v })}
                  />
                  <KitNum
                    testId={`kit-${ki}-part-${pi}-dx`}
                    value={pt.dx}
                    onChange={(v) => setField({ dx: v })}
                  />
                  <KitNum
                    testId={`kit-${ki}-part-${pi}-dy`}
                    value={pt.dy}
                    onChange={(v) => setField({ dy: v })}
                  />
                  <button
                    type="button"
                    data-testid={`kit-${ki}-part-${pi}-delete`}
                    onClick={() =>
                      set({
                        kits: d.kits.map((k, idx) =>
                          idx === ki
                            ? {
                                ...k,
                                parts: k.parts.filter((_, j) => j !== pi),
                              }
                            : k,
                        ),
                      })
                    }
                    className="rounded-lg border border-zinc-700 p-2 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
                  >
                    <X size={13} weight="bold" />
                  </button>
                </div>
              );
            })}
            <Button
              data-testid={`kit-${ki}-add-part`}
              variant="outline"
              onClick={() =>
                set({
                  kits: d.kits.map((k, idx) =>
                    idx === ki
                      ? {
                          ...k,
                          parts: [
                            ...(k.parts || []),
                            { kind: "Faucet hole", w: 3, h: 3, dx: 0, dy: 0 },
                          ],
                        }
                      : k,
                  ),
                })
              }
              className="h-9 border-dashed border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
            >
              <Plus size={13} weight="bold" className="mr-1.5" /> Add part
            </Button>
          </div>
        </div>
      ))}
    </div>
    <Button
      data-testid="add-kit"
      variant="outline"
      onClick={() =>
        set({
          kits: [
            ...(d.kits || []),
            {
              id: `kit-${Date.now()}`,
              name: "New kit",
              parts: [{ kind: "Undermount sink", w: 30, h: 18, dx: 0, dy: 0 }],
            },
          ],
        })
      }
      className="mt-4 h-11 border-dashed border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
    >
      <Plus size={14} weight="bold" className="mr-1.5" /> Add kit
    </Button>
  </div>
);
